<script lang="ts">
    import { faTimes, faCopy, faAsterisk, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
    import { createEventDispatcher, onMount, tick } from 'svelte'
    import Fa from 'svelte-fa'
    import Zbutton from './zbutton.svelte'
    import ZinputMultiselect from './zinput/zinput-multiselect.svelte'
    import ZinputSelect from './zinput/zinput-select.svelte'
    import ZinputTime from './zinput/zinput-time.svelte'
    import ZinputDate from './zinput/zinput-date.svelte'
    import ZinputDatetime from './zinput/zinput-datetime.svelte'
    import ZinputMonth from './zinput/zinput-month.svelte'
    import ZinputNumber from './zinput/zinput-number.svelte'
    import ZinputToggle from './zinput/zinput-toggle.svelte'
    import ZinputText from './zinput/zinput-text.svelte'
    import ZinputTextarea from './zinput/zinput-textarea.svelte'
    import ZinputGroupselect from './zinput/zinput-groupselect.svelte'
    import ZinputRadioselect from './zinput/zinput-radioselect.svelte'
    import ZinputPalette from './zinput/zinput-palette.svelte'
    import ZinputColor from './zinput/zinput-color.svelte'
    import ZinputInlineselect from './zinput/zinput-inlineselect.svelte'
    import ZinputInlineselect2 from './zinput/zinput-inlineselect-2.svelte'
    import ZinputFile from './zinput/zinput-file.svelte'
    import type { GridButton } from './zgrid'
    const dispatch = createEventDispatcher()

    let className = ''
    export { className as class }
    export let label = ''
    export let value: any = null
    export let type:
        | 'text'
        | 'number'
        | 'date'
        | 'datetime'
        | 'select'
        | 'toggle'
        | 'password'
        | 'multiselect'
        | 'groupselect'
        | 'radioselect'
        | 'palette'
        | 'color'
        | 'inlineselect'
        | 'inlineselect_2'
        | 'month'
        | 'tel'
        | 'textarea'
        | 'time'
        | 'file'
        // campo de busca: mesmo input de texto, mas com type=search no DOM — além de semântico, é o
        // que segura o autopreenchimento do navegador (junto com autocomplete="off" de quem usa)
        | 'search' = 'text'
    export let palette: string = 'pastel'
    export let options: { value: any; label: string; group?: string; disabled?: boolean }[] = []
    export let size: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 = null
    export let sizeM: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 = 12
    export let disabled: boolean = false
    export let required: boolean = false
    export let placeholder: string = ''
    export let replace: { original: string; replace: string }[] = []
    export let help: string = ''
    export let showCopy: boolean = false
    export let showClear: boolean = false
    export let rows = undefined
    export let acts: GridButton[] = []
    export let autocomplete: string = null
    // name/id do input nativo — hoje só o login usa, para o gerenciador de senhas do navegador
    // reconhecer o par usuário/senha (vale para os tipos de texto)
    export let name: string = null
    export let id: string = null
    // repassado ao campo de texto (ver zinput-text)
    export let maxlength: number = null
    export let hideNested: boolean = false
    export let multi: boolean = false
    export let skin: 'default' | 'soft' | 'outline' = 'default'
    // marca o campo para o usuário olhar: 'attention' (verde) é o que ficou pendente ou mudou — os campos
    // que a importação do XML da nota não conseguiu preencher, por exemplo; 'danger' (vermelho) é erro de
    // validação antes de enviar. quem destaca é quem sabe: o destaque sai só quando esta propriedade muda,
    // nunca sozinho ao digitar — senão a lista de pendências se desfaz enquanto o usuário ainda a percorre
    export let highlight: '' | 'attention' | 'danger' = ''
    // só para type="file": false sobe o arquivo para o bucket público, com URL aberta (ver zinput-file)
    export let presign: boolean = true
    // só para type="file": reduz imagem para caber nestes limites antes de subir, mantendo a
    // proporção. zero (padrão) desliga a redução (ver zinput-file)
    export let maxWidth: number = 0
    export let maxHeight: number = 0

    const internalID = 'id_' + Math.random() * 999999

    let htmlInput

    let _value: any = ''
    $: {
        importValue(value, type)
    }

    $: {
        exportValue(_value)
    }

    export function focus() {
        try {
            htmlInput.focus()
        } catch (error) {}
    }

    let exporting = false
    let importing = false

    function importValue(v, t) {
        if (exporting) return
        importing = true
        try {
            if (type === 'text' || type === 'search') {
                if (v === undefined || v === null) {
                    value = ''
                    return
                }
                _value = v
                return
            }
            if (type === 'select') {
                if (v === undefined) {
                    value = null
                    return
                }
            }
            _value = v
        } catch (error) {
        } finally {
            evaluateMap()
            setTimeout(() => (importing = false), 20)
        }
    }

    function evaluateMap() {
        const found = replace.find((el) => el.original === _value)
        if (found) _value = found.replace
    }

    function exportValue(v) {
        if (importing) return
        if (value === v) return
        exporting = true
        try {
            value = v
        } catch (error) {
        } finally {
            setTimeout(() => (exporting = false), 30)
        }
    }

    function setFocus() {
        htmlInput.focus()
    }

    onMount(async () => {
        importValue(value, type)
        await tick()
        setTimeout(() => {
            isEmpty = detectIsEmpty(_value)
        }, 50)
    })

    async function goOnKeyup(ev) {
        await tick()
        dispatch('keyup', ev)
    }

    async function goChange(ev) {
        await tick()
        ev.checked = _value
        ev.value = _value
        dispatch('change', ev)
    }

    async function goFocus(ev) {
        await tick()
        dispatch('focus', ev)
    }

    async function goBlur(ev) {
        await tick()
        dispatch('blur', ev)
    }

    async function goClear() {
        // campo de texto vazio é string vazia, não null: o importValue daqui já trata null como '' nesses
        // tipos, então limpar com null criava um estado passageiro que não existe em nenhum outro momento
        // — e quem recebe o valor por bind (ex.: a "query" do zinput-search-trigger) estourava ao ler
        // .length antes da normalização acontecer
        _value = ['text', 'password', 'tel', 'search'].includes(type) ? '' : null
        dispatch('clear')
        await tick()
        // o clique no "x" tira o foco do campo; devolver é o que se espera de um limpar — principalmente
        // na busca, onde limpar serve justamente para digitar outra coisa
        try {
            htmlInput?.focus()
        } catch (error) {}
    }

    let copied = false
    function goCopy() {
        window.api.utils.utils.copyToClipboard(_value)
        copied = true
    }

    let tooltip
    let showTooltip = false
    async function goShowTooltip(ev) {
        showTooltip = !showTooltip
        if (!showTooltip) return
        await tick()

        tooltip.style.top = 0
        tooltip.style.left = 0
        let top = ev.clientY - tooltip.clientHeight
        let left = ev.clientX - tooltip.clientWidth
        if (left + tooltip.clientWidth > window.innerWidth - 8) {
            left = window.innerWidth - 8
        }
        if (left - 16 < 0) left = 24
        tooltip.style.top = top + 32 + 'px'
        tooltip.style.left = left + 32 + 'px'
    }

    // "options" entra aqui de propósito: é comum as opções chegarem depois do valor (carregadas da API),
    // e sem ela na expressão o Svelte não recalcularia quando elas mudam
    $: isEmpty = detectIsEmpty(value, options)

    function detectIsEmpty(v, opts = options) {
        // o select próprio (zinput-select) mostra só o rótulo da opção escolhida: sem opção que
        // corresponda ao valor, a caixa fica vazia e o rótulo desce pro lugar do valor, como num campo
        // de texto. comparar com as opções (e não só "!v") mantém como preenchido um valor legítimo
        // como 0, false ou '' quando existe uma opção para ele (ex.: "Todos" com value '')
        if (type === 'select') return !opts?.some((el) => el.value === v)
        // as opções ficam sempre visíveis (não é um placeholder que ocupa o lugar da label vazia),
        // então o label sempre fica na posição reduzida, como os demais tipos "select".
        if (type === 'inlineselect' || type === 'inlineselect_2') return false
        // a amostra de cor fica sempre visivel, entao o label nunca ocupa o lugar do valor
        if (type === 'color') return false
        if (!v && v !== 0) return true
        if (Array.isArray(v) && v.length === 0) return true
        return false
    }
</script>

<div
    class="zinput zinput_{skin} z-col z-col_w-{size} z-col-m_w-{sizeM} zinput_type-{type} {className}"
    class:zinput-empty={isEmpty}
    class:zinput-disabled={disabled}
    class:zinput-unlabeled={!label}
    class:zinput-highlight-attention={highlight === 'attention'}
    class:zinput-highlight-danger={highlight === 'danger'}
>
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    {#if label}
        <label on:click={setFocus} for="" class="zinput_label">
            {label}
            {#if !disabled && required}
                <span class="zinput_required">
                    <Fa icon={faAsterisk}></Fa>
                </span>
            {/if}
        </label>
    {/if}
    {#if type === 'text' || type === 'password' || type === 'tel' || type === 'search'}
        <ZinputText
            bind:value={_value}
            inputType={type}
            {disabled}
            {required}
            {autocomplete}
            {name}
            {id}
            {maxlength}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'textarea'}
        <ZinputTextarea
            bind:value={_value}
            {disabled}
            {required}
            {rows}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'number'}
        <ZinputNumber
            bind:value
            {disabled}
            {required}
            {autocomplete}
            {placeholder}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'date'}
        <ZinputDate
            bind:value
            {disabled}
            {required}
            {autocomplete}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'datetime'}
        <ZinputDatetime
            bind:value
            {disabled}
            {required}
            {autocomplete}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'time'}
        <ZinputTime
            bind:value
            {disabled}
            {required}
            {autocomplete}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'month'}
        <ZinputMonth
            bind:value
            {disabled}
            {required}
            {autocomplete}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'select'}
        <!-- o select é nosso (zinput-select.svelte), não o do sistema: autocomplete do navegador
             não se aplica -->
        <ZinputSelect
            bind:value={_value}
            {options}
            {disabled}
            {required}
            {label}
            labeled={!!label}
            bind:htmlInput
            on:keyup={goOnKeyup}
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}
    {#if type === 'multiselect'}
        <ZinputMultiselect bind:value {options} {disabled} {label} labeled={!!label} bind:htmlInput on:change={goChange} />
    {/if}
    {#if type === 'groupselect'}
        <ZinputGroupselect {hideNested} bind:value {options} {disabled} {label} labeled={!!label} bind:htmlInput on:change={goChange} />
    {/if}
    {#if type === 'radioselect'}
        <ZinputRadioselect bind:value {options} {disabled} {label} labeled={!!label} bind:htmlInput on:change={goChange} />
    {/if}
    {#if type === 'palette'}
        <ZinputPalette {palette} bind:value {disabled} {label} labeled={!!label} bind:htmlInput on:change={goChange} />
    {/if}
    {#if type === 'color'}
        <ZinputColor bind:value {disabled} {required} labeled={!!label} bind:htmlInput on:change={goChange} />
    {/if}
    {#if type === 'inlineselect'}
        <ZinputInlineselect bind:value {options} {disabled} {multi} labeled={!!label} bind:htmlInput on:change={goChange} />
    {/if}
    {#if type === 'inlineselect_2'}
        <ZinputInlineselect2 bind:value {options} {disabled} {multi} labeled={!!label} bind:htmlInput on:change={goChange} />
    {/if}
    {#if type === 'toggle'}
        <ZinputToggle bind:value {disabled} {autocomplete} {label} on:change={goChange} />
    {/if}
    {#if type === 'file'}
        <ZinputFile
            bind:value={_value}
            {options}
            {multi}
            {disabled}
            {required}
            {presign}
            {maxWidth}
            {maxHeight}
            labeled={!!label}
            bind:htmlInput
            on:change={goChange}
            on:focus={goFocus}
            on:blur={goBlur}
        />
    {/if}

    {#if showClear && value && (!Array.isArray(value) || value.length > 0) && ['text', 'password', 'tel', 'file', 'search'].includes(type)}
        <div class="zinput_copy" title="Limpar" on:click={goClear}>
            <Fa icon={faTimes}></Fa>
        </div>
    {/if}

    {#if showCopy}
        <div class="zinput_copy" title="Copiar" class:zinput_copy-copied={copied} on:click={goCopy}>
            <Fa icon={faCopy}></Fa>
        </div>
    {/if}

    {#if help}
        <div class="zinput_tooltip" on:click={goShowTooltip}>
            <Fa icon={faQuestionCircle}></Fa>
        </div>
    {/if}
    {#each acts as act}
        <!-- act quase sempre é só ícone: o title é o que aparece no hover e o que o leitor de tela anuncia -->
        <Zbutton class={act.class} icon={act.icon ? act.icon : null} on:click={() => act.callback()} label={act.label} title={act.title} color={act.color}
        ></Zbutton>
    {/each}
    {#if showTooltip}
        <div on:mouseleave={goShowTooltip} bind:this={tooltip} class="zinput_tooltip_content zinput_tooltip_content-show-{showTooltip}">
            <div class="zinput_tooltip_content_text">{help}</div>
        </div>
    {/if}
</div>
